import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import BackButton from '../../../components/Common/BackButton';
import { FinalSurveyResponses } from '../../../interfaces/teacher.interface';
import Loader from '../../../components/Common/Loader';

export interface OnboardingFinalSurveyProps {
  onClose: () => void;
  onSkip: () => void;
  onSubmit: (responses: FinalSurveyResponses) => void;
  onBack: () => void;
  loader: boolean;
}

const OnboardingFinalSurvey: React.FC<OnboardingFinalSurveyProps> = ({ onClose, onSubmit, onBack, onSkip, loader }) => {
  const user = useSelector((state: RootState) => state.user.user);

  const [responses, setResponses] = useState<FinalSurveyResponses>({
    readiness: null,
    confidence: null,
    storyChoice: null,
    customSupport: null,
    writingApproach: null,
  });

  const [showReadinessComment, setShowReadinessComment] = useState(false);
  const [showConfidenceComment, setShowConfidenceComment] = useState(false);
  const [showStoryType, setShowStoryType] = useState(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  useEffect(() => {
    const isBasicFieldsValid =
      !!responses.readiness &&
      !!responses.confidence &&
      !!responses.storyChoice &&
      !!responses.customSupport &&
      !!responses.writingApproach;

    // Check conditional text requirements
    const isReadinessCommentValid = responses.readiness === 'not_yet' ? !!responses.readinessComment?.trim() : true;
    const isConfidenceCommentValid = responses.confidence === 'not_yet' ? !!responses.confidenceComment?.trim() : true;
    const isStoryTypeValid = responses.storyChoice === 'yes' ? !!responses.storyType?.trim() : true;

    setIsFormValid(isBasicFieldsValid && isReadinessCommentValid && isConfidenceCommentValid && isStoryTypeValid);
  }, [responses]);

  const handleReadinessChange = (value: 'clear' | 'not_yet') => {
    setResponses((prev) => ({ ...prev, readiness: value, readinessComment: '' }));
    setShowReadinessComment(value === 'not_yet');
  };

  const handleConfidenceChange = (value: 'confident' | 'not_yet') => {
    setResponses((prev) => ({ ...prev, confidence: value, confidenceComment: '' }));
    setShowConfidenceComment(value === 'not_yet');
  };

  const handleStoryChoiceChange = (value: 'yes' | 'no') => {
    setResponses((prev) => ({ ...prev, storyChoice: value, storyType: '' }));
    setShowStoryType(value === 'yes');
  };

  const handleSubmit = () => {
    if (!isFormValid) {
      return;
    }
    onSubmit(responses);
  };

  const handleSkip = () => {
    onSkip();
  };

  return (
    <div className="relative w-full flex flex-col max-h-screen">
      {/* Header */}
      <div className="mb-6 px-1 sm:px-3 lg:px-6">
        <p className="text-secondaryStoryDescriptionGreyTextColor text-sm">5 quick questions (takes under a minute)</p>
      </div>

      {/* Questions */}
      <div className="flex flex-col overflow-y-auto max-h-[60vh] space-y-6 px-1 sm:px-3 lg:px-6">
        {/* Q1. Readiness to Run */}
        <div>
          <h3 className="font-semibold text-black mb-3">
            Q1. Do you feel clear about what to do next to get your class ready and run BuildMyStory?
          </h3>
          <div className="space-y-2 text-secondaryStoryBlackText">
            <label className="flex items-center">
              <input
                type="radio"
                name="readiness"
                value="clear"
                checked={responses.readiness === 'clear'}
                onChange={() => handleReadinessChange('clear')}
                className="mr-3 w-4 h-4"
              />
              <span>Yes, I'm clear</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="readiness"
                value="not_yet"
                checked={responses.readiness === 'not_yet'}
                onChange={() => handleReadinessChange('not_yet')}
                className="mr-3 w-4 h-4"
              />
              <span>Not yet</span>
            </label>
          </div>
          <div className={`mt-3 ${!showReadinessComment ? 'hidden' : ''}`}>
            <textarea
              placeholder="What would make you feel more ready?"
              value={responses.readinessComment || ''}
              onChange={(e) => setResponses((prev) => ({ ...prev, readinessComment: e.target.value }))}
              className="w-full p-3 border border-secondaryStoryGrayBorder rounded-lg resize-none"
              rows={1}
            />
          </div>
        </div>

        {/* Q2. Student Support */}
        <div>
          <h3 className="font-semibold text-black mb-3">
            Q2. Do you feel confident helping your students use BuildMyStory?
          </h3>
          <div className="space-y-2 text-secondaryStoryBlackText">
            <label className="flex items-center">
              <input
                type="radio"
                name="confidence"
                value="confident"
                checked={responses.confidence === 'confident'}
                onChange={() => handleConfidenceChange('confident')}
                className="mr-3 w-4 h-4"
              />
              <span>Yes, I feel confident</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="confidence"
                value="not_yet"
                checked={responses.confidence === 'not_yet'}
                onChange={() => handleConfidenceChange('not_yet')}
                className="mr-3 w-4 h-4"
              />
              <span>Not yet</span>
            </label>
          </div>
          {showConfidenceComment && (
            <div className="mt-3">
              <textarea
                placeholder="What would make you feel more confident?"
                value={responses.confidenceComment || ''}
                onChange={(e) => setResponses((prev) => ({ ...prev, confidenceComment: e.target.value }))}
                className="w-full p-3 border border-secondaryStoryGrayBorder rounded-lg resize-none"
                rows={1}
              />
            </div>
          )}
        </div>

        {/* Q3. Story Choice (critical) */}
        <div>
          <h3 className="font-semibold text-black mb-3">
            Q3. Do you already know what kind of story your students will be working on?
          </h3>
          <div className="space-y-2 text-secondaryStoryBlackText">
            <label className="flex items-center">
              <input
                type="radio"
                name="storyChoice"
                value="yes"
                checked={responses.storyChoice === 'yes'}
                onChange={() => handleStoryChoiceChange('yes')}
                className="mr-3 w-4 h-4"
              />
              <span>Yes</span>
            </label>
            <div className={`mt-3 ${!showStoryType ? 'hidden' : ''}`}>
              <textarea
                placeholder="What kind of book will your students create? (e.g. Animal Report, Biography, How-To, Friendship Story)"
                value={responses.storyType || ''}
                onChange={(e) => setResponses((prev) => ({ ...prev, storyType: e.target.value }))}
                className="w-full p-3 border border-secondaryStoryGrayBorder rounded-lg resize-none"
                rows={2}
              />
            </div>
            <label className="flex items-center">
              <input
                type="radio"
                name="storyChoice"
                value="no"
                checked={responses.storyChoice === 'no'}
                onChange={() => handleStoryChoiceChange('no')}
                className="mr-3 w-4 h-4"
              />
              <span>No, I'd like some ideas</span>
            </label>
          </div>
        </div>

        {/* Q4. Custom Support */}
        <div>
          <h3 className="font-semibold text-black mb-3">
            Q4. Would you like us to create custom writing sheets or lesson guides for your class?
          </h3>
          <div className="space-y-2 text-secondaryStoryBlackText">
            <label className="flex items-center">
              <input
                type="radio"
                name="customSupport"
                value="yes"
                checked={responses.customSupport === 'yes'}
                onChange={(e) => setResponses((prev) => ({ ...prev, customSupport: e.target.value as 'yes' | 'no' }))}
                className="mr-3 w-4 h-4"
              />
              <span>Yes, that would help</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="customSupport"
                value="no"
                checked={responses.customSupport === 'no'}
                onChange={(e) => setResponses((prev) => ({ ...prev, customSupport: e.target.value as 'yes' | 'no' }))}
                className="mr-3 w-4 h-4"
              />
              <span>No thanks, I'm all set for now</span>
            </label>
          </div>
        </div>

        {/* Q5. Writing Approach */}
        <div>
          <h3 className="font-semibold text-black mb-3">Q5. How will your students write their books?</h3>
          <div className="space-y-2 text-secondaryStoryBlackText">
            <label className="flex items-center">
              <input
                type="radio"
                name="writingApproach"
                value="full_story"
                checked={responses.writingApproach === 'full_story'}
                onChange={(e) => setResponses((prev) => ({ ...prev, writingApproach: e.target.value as any }))}
                className="mr-3 w-4 h-4 min-w-4 min-h-4"
              />
              <span>They will write the full story themselves</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="writingApproach"
                value="ai_assisted"
                checked={responses.writingApproach === 'ai_assisted'}
                onChange={(e) => setResponses((prev) => ({ ...prev, writingApproach: e.target.value as any }))}
                className="mr-3 w-4 h-4 min-w-4 min-h-4"
              />
              <span>They will share their ideas and use AI to turn those ideas into a story</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="writingApproach"
                value="not_sure"
                checked={responses.writingApproach === 'not_sure'}
                onChange={(e) => setResponses((prev) => ({ ...prev, writingApproach: e.target.value as any }))}
                className="mr-3 w-4 h-4 min-w-4 min-h-4"
              />
              <span>Not sure yet</span>
            </label>
          </div>
        </div>
      </div>

      {/* Footer buttons */}
      <div className="flex justify-between items-center mt-8 px-1 sm:px-3 lg:px-6">
        <div>
          <BackButton onClick={onBack}></BackButton>
        </div>

        <div className="flex gap-3">
          <button onClick={handleSkip} className="px-4 py-2 text-black hover:text-gray-800">
            Skip
          </button>

          <button
            className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center text-sm sm:text-base w-full sm:w-auto justify-center ${
              isFormValid ? '' : 'opacity-50 cursor-not-allowed'
            }`}
            onClick={handleSubmit}
            disabled={!isFormValid || loader}
          >
            Continue
            <Loader loader={loader} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFinalSurvey;
